import { cx } from 'class-variance-authority';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import { head } from 'lodash';
import moment from 'moment';
import { FC, Key, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useSWR from 'swr';
import { useClubSlug, useSettings } from '../../Clubs/hooks';
import { SpaceGrid } from '../Views/TableView';
import { getLengthOptions, renderMinutesFromTime } from '../helpers';

interface Props {
    timeIntervals: number;
    spaceId: Key;
    slotWidth: number;
    clubSlug?: string;
}

const LengthInput: FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
    timeIntervals,
    spaceId,
    clubSlug: _clubSlug,
    slotWidth,
}) => {
    const { watch, setValue } = useFormContext();
    const clubSlug = useClubSlug(_clubSlug);
    const start: Date | undefined = watch('start');
    const end: Date | undefined = watch('end');
    const gridDate = useMemo(() => (start ? moment(start).startOf('day').toISOString() : undefined), [start]);
    const [initialized, setInitialized] = useState(false);
    const [isStartInputFocused, setIsStartInputFocused] = useState(false);
    const [isEndInputFocused, setIsEndInputFocused] = useState(false);
    const { data, isLoading: isLoadingData } = useSWR<{
        data: SpaceGrid[];
    }>(gridDate ? `club/${clubSlug}/space-manager/space/grid?date=${gridDate}` : null);
    const [
        { max_length, min_length } = {
            max_length: undefined,
            min_length: undefined
        },
        { isLoading: isLoadingSettings }
    ] = useSettings('space_manager.booking', clubSlug);
    const isLoading = false || isLoadingSettings || isLoadingData;
    const availableStartTimes = useMemo(() => {
        // Actual bookable start times for the selected space/day (can vary over the day).
        return (data?.data?.find((space) => space.id == spaceId)?.slots ?? [])
            // trainer mode: a slot nobody can teach is not a start time
            .filter((slot) => slot.data === null && !slot.unavailable)
            .map((slot) => slot.start)
            .sort((a, b) => a - b);
    }, [data, spaceId]);

    const selectedIndex = useMemo(() => {
        const startDate = moment(start);
        const endDate = moment(end);

        return Math.round(endDate.diff(startDate, 'minutes') / timeIntervals);
    }, [start, end, timeIntervals]);

    const maxStartTime = useMemo(() => {
        const slots = data?.data?.find((space) => space.id == spaceId)?.slots ?? [];
        if (slots?.length <= 1) return undefined;
        let index = slots?.findIndex((slot) => slot.start === renderMinutesFromTime(start));
        while (
            index < slots?.length - 1 &&
            slots[index + 1]?.data === null &&
            // so the offered lengths stop at the trainer's shift edge, not at the next booking
            !slots[index + 1]?.unavailable &&
            slots[index + 1]?.start === slots[index]?.start + slots[index]?.width
        ) {
            index++;
        }

        return slots?.at(index)?.start ?? 0;
    }, [data, start]);

    const startMinutes = renderMinutesFromTime(start);
    const maxStartTimeFallback = useMemo(() => {
        if (!start || !Number.isFinite(slotWidth)) return undefined;
        return startMinutes + Math.max(slotWidth - timeIntervals, 0);
    }, [start, slotWidth, timeIntervals, startMinutes]);

    const maxStartTimeEffective = useMemo(() => {
        if (maxStartTime === undefined) return maxStartTimeFallback;
        if (maxStartTimeFallback === undefined) return maxStartTime;
        return Math.max(maxStartTime, maxStartTimeFallback);
    }, [maxStartTime, maxStartTimeFallback]);

    // How many consecutive free intervals the selected start actually offers.
    const availableIntervals = useMemo(() => {
        if (!Number.isFinite(maxStartTimeEffective) || maxStartTimeEffective < startMinutes) return 0;
        return Math.floor((maxStartTimeEffective - startMinutes) / timeIntervals) + 1;
    }, [maxStartTimeEffective, startMinutes, timeIntervals]);

    // Memoized so the snapping effects below do not re-run on every render.
    const options = useMemo(
        () =>
            getLengthOptions({
                interval: timeIntervals,
                availableIntervals,
                minLength: min_length,
                maxLength: max_length
            }),
        [timeIntervals, availableIntervals, min_length, max_length]
    );

    useEffect(() => {
        const handleFocusIn = (event: FocusEvent) => {
            const target = event.target;
            if (!(target instanceof HTMLInputElement)) return;
            if (target.name === 'start') {
                setIsStartInputFocused(true);
            } else if (target.name === 'end') {
                setIsEndInputFocused(true);
            }
        };

        const handleFocusOut = (event: FocusEvent) => {
            const target = event.target;
            if (!(target instanceof HTMLInputElement)) return;
            if (target.name === 'start') {
                setIsStartInputFocused(false);
            } else if (target.name === 'end') {
                setIsEndInputFocused(false);
            }
        };

        window.addEventListener('focusin', handleFocusIn);
        window.addEventListener('focusout', handleFocusOut);

        return () => {
            window.removeEventListener('focusin', handleFocusIn);
            window.removeEventListener('focusout', handleFocusOut);
        };
    }, []);

    useEffect(() => {
        // Do not snap while the user is still typing the start time.
        if (isStartInputFocused) return;
        if (!start || !end || availableStartTimes.length <= 0) return;

        const startMinutes = renderMinutesFromTime(start);
        if (!Number.isFinite(startMinutes)) return;
        if (availableStartTimes.includes(startMinutes)) return;

        // Snap only to the next valid slot start.
        const snappedStartMinutes = availableStartTimes.find((slotStart) => slotStart >= startMinutes);
        if (snappedStartMinutes === undefined) return;

        // Keep current booking duration when adjusting start.
        const durationMinutes = moment(end).diff(moment(start), 'minutes');
        if (!Number.isFinite(durationMinutes)) return;

        const duration = Math.max(durationMinutes, timeIntervals);
        const snappedStart = moment(start).startOf('day').add(snappedStartMinutes, 'minutes');

        setValue('start', snappedStart.toDate());
        setValue('end', snappedStart.clone().add(duration, 'minutes').toDate());
    }, [availableStartTimes, start, end, timeIntervals, setValue, isStartInputFocused]);

    useEffect(() => {
        // Avoid interrupting user typing in either time input.
        if (isStartInputFocused || isEndInputFocused) return;
        if (!start || !end || options.length <= 0) return;

        const durationMinutes = moment(end).diff(moment(start), 'minutes');
        if (!Number.isFinite(durationMinutes)) return;

        const allowedDurations = options.map((option) => option * timeIntervals);
        if (allowedDurations.includes(durationMinutes)) return;

        const snappedDuration =
            allowedDurations.find((allowed) => allowed >= durationMinutes) ?? allowedDurations.at(-1);
        if (!snappedDuration) return;

        setValue('end', moment(start).add(snappedDuration, 'minutes').toDate());
    }, [start, end, options, timeIntervals, setValue, isStartInputFocused, isEndInputFocused]);

    useEffect(() => {
        if (!initialized && !isLoading && start && end && options?.length > 0) {
            const currentLength = Math.round(moment(end).diff(moment(start), "minutes") / timeIntervals);
            const nextLength = options.includes(currentLength) ? currentLength : head(options);
            setValue("end", moment(start).add(nextLength * timeIntervals, "minutes").toDate());
            setInitialized(true);
        }
    }, [initialized, options, start, end, timeIntervals, isLoading, setValue]);

    // Guards must stay below every hook — an early return above them breaks the hook order.
    if (!clubSlug || !start) return null;
    // Keep the skeletons visible while loading; only hide once we know there is nothing to offer.
    if (!isLoading && options.length <= 0) return null;

    return (
        <div className="tw-mt-4 tw-grid tw-grid-cols-3 tw-flex-wrap tw-gap-4">
            <LayoutGroup>
                <AnimatePresence initial={false} mode="popLayout">
                    {isLoading
                        ? Array(3)
                              .fill(true)
                              .map((_, i) => (
                                  <motion.div
                                      layout
                                      key={`loading_${i}`}
                                      initial={{ opacity: 0 }}
                                      animate={{ opacity: 1 }}
                                      exit={{ opacity: 0 }}
                                      className="tw-card-flat tw-selectable tw-h-[2.7rem] tw-animate-pulse tw-p-2"
                                  />
                              ))
                        : options.map((i, index) => (
                              <motion.div
                                  layout
                                  key={`${i}_${index}`}
                                  initial={{ opacity: 0, y: 4 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -4 }}
                                  transition={{ layout: { duration: 0.2 } }}
                                  className={cx(
                                      selectedIndex === i && 'tw-selected',
                                      'tw-card-flat tw-selectable tw-card-clickable tw-min-w-fit tw-p-2'
                                  )}
                                  onClick={() =>
                                      setValue(
                                          'end',
                                          moment(start)
                                              .add(i * timeIntervals, 'minutes')
                                              .toDate()
                                      )
                                  }
                              >
                                  <span className="tw-inline-block tw-w-full tw-whitespace-nowrap tw-text-center">
                                      {i * timeIntervals} Minuten
                                  </span>
                              </motion.div>
                          ))}
                </AnimatePresence>
            </LayoutGroup>
        </div>
    );
};

export default LengthInput;
